import { COOKIE_KEYS } from "@/utils/cookie-utils";

/** 세션 키 선복원 대기 상한. 넘으면 기다리지 않고 화면 렌더링을 진행합니다. */
export const RESTORE_SESSION_KEY_TIMEOUT_MS = 8_000;

interface SessionKeyRestorable {
  getKey(): CryptoKey | null;
  getOrRestoreKey(): Promise<CryptoKey | null>;
}

/**
 * 로그인 세션(역할 쿠키)이 있으면 화면이 API 를 호출하기 전에 세션 키를 먼저 복원합니다.
 *
 * 새로고침 직후 액세스 토큰이 만료된 상태에서 암호화 API(/s/)와 키 재교환(/auth/keys)이 동시에 나가면,
 * 토큰 재발급 후 재시도된 암호화 API 가 키 복원을 기다리고, 키 재교환은 재발급 대기열에서
 * 그 재시도가 끝나기를 기다리는 교착 상태가 됩니다. 키 재교환을 단독으로 먼저 끝내 이를 막습니다.
 * 복원에 실패하거나 상한 시간을 넘겨도 초기화는 계속 진행하며, 이후 화면의 권한 확인에서 로그인 페이지로 이동합니다.
 *
 * @returns 키 복원 결과 (쿠키가 없거나 이미 키가 있으면 "skipped")
 */
export const restoreSessionKeyBeforeRender = async (
  holder: SessionKeyRestorable,
  cookie: string | undefined = typeof document === "undefined" ? undefined : document.cookie,
  timeoutMs: number = RESTORE_SESSION_KEY_TIMEOUT_MS
): Promise<"skipped" | "restored" | "failed" | "timeout"> => {
  if (cookie === undefined || holder.getKey()) {
    return "skipped";
  }

  const hasUserRoleCookie = cookie
    .split(";")
    .some((item) => item.trim().startsWith(`${COOKIE_KEYS.USER_ROLE}=`));
  if (!hasUserRoleCookie) {
    return "skipped";
  }

  console.log("[INITIALIZE_INSTANCES] :: restoreSessionKeyBeforeRender :: 세션 키 선복원 시작");
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<"timeout">((resolve) => {
    timer = setTimeout(() => resolve("timeout"), timeoutMs);
  });
  const restore = holder
    .getOrRestoreKey()
    .then((key): "restored" | "failed" => (key ? "restored" : "failed"))
    .catch((): "failed" => "failed");

  const result = await Promise.race([restore, timeout]);
  clearTimeout(timer);
  console.log(`[INITIALIZE_INSTANCES] :: restoreSessionKeyBeforeRender :: 세션 키 선복원 ${result}`);
  return result;
};
